import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { ApiRoutes } from '@sb/library/api';
import { BaseViewComponent, LoremIpsumText, PaginatorData, SnackBarDuration, SnackbarData, WebsiteBlockAction, WebsiteBlockImageAction, WebsiteBlockImageIndexItem, WebsiteBlockTypeData, WebsiteBookingConfigurationDialogData, WebsiteMode, WebsitePageScope, photoGalleryCarouselMaxItems } from '@sb/library/base';
import { CalendarBookingSearchOptions, DesignBlockMode, UniqueID, WebsiteBlockContactFormType, WebsiteBlockDisplayItem, WebsiteBlockEmbedContentType, WebsiteBlockLoginFormType, WebsiteBlockTextAndMediaDisplayItem, WebsiteBlockTextAndMediaImageDisplayItem, WebsiteBlockTextAndMediaType, WebsiteBlockType, WebsiteDisplayItem, WebsiteHostingType, WebsiteMediaLibraryDisplayItem, WebsitePageDisplayItem, WebsiteRenderContextDisplayItem, WebsiteScriptCodeDisplayItem, WebsiteTemplate } from '@sb/library/data-models';
import { SBDate } from '@sb/library/localization';
import { WebsiteManager } from '@sb/library/managers';
import { forkJoin } from 'rxjs';
import { CloneBlockDialogComponent, WebsiteBlockCloneBlockScope } from './clone-block-dialog/clone-block-dialog.component';
import { ConfigurationErrorDialogComponent } from './configuration-error-dialog/configuration-error-dialog.component';
import { SnackbarService } from '@sb/library/snackbar';

const listAnimation = trigger('listAnimation', [
    transition('* <=> *', [
        query(':enter',
            [style({ opacity: 0 }), stagger('60ms', animate('600ms ease-out', style({ opacity: 1 })))],
            { optional: true }
        ),
        query(':leave',
            animate('200ms', style({ opacity: 0 })),
            { optional: true}
        )
    ])
]);

@Component({
    selector: 'lib-website-page',
    templateUrl: './website-page.component.html',
    styleUrls: ['./website-page.component.scss'],
    animations: [listAnimation]
})
export class WebsitePageComponent extends BaseViewComponent implements WebsitePageScope, WebsiteBlockCloneBlockScope, OnInit {

    Scope = this.SetScope({
        Implements: [WebsitePageScope, WebsiteBlockCloneBlockScope]
    });

    @ContentChild('textMediaBlockHeader') textMediaBlockHeader!: TemplateRef<any>;
    @ContentChild('textMediaBlockBody') textMediaBlockBody!: TemplateRef<any>;
    @ContentChild('textMediaBlockImage') textMediaBlockImage!: TemplateRef<any>;
    @ContentChild('textMediaBlockImageHeader') textMediaBlockImageHeader!: TemplateRef<any>;
    @ContentChild('textMediaBlockImageBody') textMediaBlockImageBody!: TemplateRef<any>;
    @ContentChild('htmlBlock') htmlBlock!: TemplateRef<any>;

    private _blocks: WebsiteBlockDisplayItem[] | undefined = [];
    private _currentPageID!: string;

    readonly hostingType = WebsiteHostingType.InternalDomain;

    @Input()
    set ID(value: string) {
        this._currentPageID = value;
        this.draw = false;
        this.UpdateBlocks();
    }

    @Input() renderContext!: WebsiteRenderContextDisplayItem;
    @Input() websiteMode: WebsiteMode = WebsiteMode.Preview;

    private _websiteData!: WebsiteDisplayItem;
    @Input()
    set websiteData(value: WebsiteDisplayItem) {
        this._websiteData = value;
        this.UpdateBlocks();
    }

    @Output() pageChanged: EventEmitter<void> = new EventEmitter();
    @Output() openMediaDialog: EventEmitter<WebsiteBlockImageIndexItem> = new EventEmitter();
    @Output() openImageDetailsDialog: EventEmitter<WebsiteBlockImageIndexItem> = new EventEmitter();
    @Output() navigationPageClicked: EventEmitter<string> = new EventEmitter();
    @Output() editBlockClicked: EventEmitter<WebsiteBlockDisplayItem> = new EventEmitter();
    @Output() openMultiSelectMediaDialog: EventEmitter<WebsiteBlockTextAndMediaDisplayItem> = new EventEmitter();

    animate: boolean = false;
    draw: boolean = false;
    showFooter: boolean = true;

    private _headerLogoSource: string | null = null;

    @Input()
    set headerLogoID(value: string) {
        this._headerLogoSource = null;
        this.GetImageURL(value);
    }

    constructor(
        private _WebsiteManager: WebsiteManager,
        private _SnackbarService: SnackbarService
    ) {
        super();
    }

    UpdateBlocks(): void {
        this._blocks = this._websiteData ? this.GetPage(this._currentPageID, this.websiteData.WebsitePages)?.WebsiteBlocks : [];
        this.NormalizeOrderIndex();

        setTimeout(() => {
            this.animate = true;
            this.draw = true;
            this.DetectChanges();
        });
    }

    HandleBlockAction(block: WebsiteBlockDisplayItem, action: WebsiteBlockAction) {
        switch (action) {
            case WebsiteBlockAction.MoveUp: this.MoveBlock(block, -1); break;
            case WebsiteBlockAction.MoveDown: this.MoveBlock(block, 1); break;
            case WebsiteBlockAction.Delete: this.DeleteBlock(block); break;
            case WebsiteBlockAction.Copy: this.CloneBlock(block); break;
            case WebsiteBlockAction.Edit: this.editBlockClicked.emit(block); break;
            case WebsiteBlockAction.MediaGallery: this.openMultiSelectMediaDialog.emit(block as WebsiteBlockTextAndMediaDisplayItem); break;
        }
    }

    HandleImageAction(block: WebsiteBlockDisplayItem.WithImages, action: WebsiteBlockImageAction, index: number) {
        if (index < 0 || index > (action === WebsiteBlockImageAction.Add ? block.Images.length : block.Images.length - 1)) { throw Error('Image index out of range'); }

        switch (action) {
            case WebsiteBlockImageAction.MediaGallery: this.OpenMediaGalleryDialog(block, index); return;
            case WebsiteBlockImageAction.Details: this.OpenImageDetailsDialog(block, index); return;
            case WebsiteBlockImageAction.Delete: this.DeleteImage(block, index); return;
        }

        if (!WebsiteBlockDisplayItem.IsTextAndMedia(block) || !WebsiteBlockTextAndMediaDisplayItem.IsGalleryBlock(block) && !WebsiteBlockTextAndMediaDisplayItem.IsPhotoGalleryBlock(block)) {
            throw Error(`${action} not supported for block type ${block.constructor.name}`);
        }

        switch (action) {
            case WebsiteBlockImageAction.MoveLeft: this.MoveImage(block, index, -1); break;
            case WebsiteBlockImageAction.MoveRight: this.MoveImage(block, index, 1); break;
            case WebsiteBlockImageAction.Add: this.AddImage(block, index); break;
        }
    }

    TrackByID(index: number, item: WebsiteBlockDisplayItem) {
        return item.ID;
    }

    TrackByImage(index: number, item: WebsiteBlockTextAndMediaImageDisplayItem) {
        return item.WebsiteMediaLibraryItemID;
    }

    AnimationEnd(): void {
        this.animate = false;
        this.showFooter = true;
    }

    GoToSocialMediaSite(site: string): void {
        window.open(site, '_blank');
    }

    NavigationPageClicked(pageID: string | null): void {
        if (pageID !== this._currentPageID) {
            this.navigationPageClicked.emit(pageID!);
        }
    }

    IsPageParentOfViewableID(currentID: string, page: WebsitePageDisplayItem): boolean {
        return page.ChildPages.map(page => {return page.ID}).includes(currentID);
    }

    HasSocialLinks(item: WebsiteDisplayItem): boolean {
        return item.SocialMediaLinks.length > 0;
    }

    Dropped(event: CdkDragDrop<WebsiteBlockDisplayItem, WebsiteBlockDisplayItem, WebsiteBlockDisplayItem>) {
        const droppedIndex: number = event.currentIndex;
        const websiteBlock: WebsiteBlockDisplayItem = event.item.data;

        websiteBlock.ID = UniqueID();
        websiteBlock.OrderIndex = droppedIndex;
        websiteBlock.DesignBlockMode = DesignBlockMode.Preview;

        this._blocks!.splice(droppedIndex, 0, websiteBlock);
        this.NormalizeOrderIndex();
        this.pageChanged.emit();

        this.DetermineProperConfiguration(websiteBlock);
    }

    DetermineProperConfiguration(websiteBlock: WebsiteBlockDisplayItem): void {
        if (WebsiteBlockDisplayItem.IsSignupForm(websiteBlock) && this._WebsiteManager.IsBookingWidget(websiteBlock.SignupFormType)) {
    
            const openLessonSlots = ApiRoutes.Calendar.BookingSlots.GetAll.Call({
                Body: this.GetBookingSearchOptions(true),
                PaginatorData: PaginatorData.TotalCount
            })

            const availabilitySlots = ApiRoutes.Calendar.BookingSlots.GetAll.Call({
                Body: this.GetBookingSearchOptions(false),
                PaginatorData: PaginatorData.TotalCount
            })

            forkJoin({OpenLessonCount: openLessonSlots, AvailabilitySlotsCount: availabilitySlots}).subscribe((data) => {
                const dialogData = new WebsiteBookingConfigurationDialogData({
                    SignUpFormType: websiteBlock.SignupFormType,
                    RenderContext: this.renderContext,
                    HasVisibleTeachers: this.renderContext.SignupFormRenderContext?.PreferredTeachers.length != 0,
                    HasOpenLessonSlots: data.OpenLessonCount.TotalItemCount != 0,
                    HasTeachersWithAvailabilityAndOnlineBooking: data.AvailabilitySlotsCount.TotalItemCount != 0,
                });

                // business logic
                // we want to open the dialog if
                // book from availability => does not have any teacher with availability & online booking enables or does not have any teachers with visible public forms
                // book from calendar => does not have any teacher with open lesson slots or does not have any teachers w/ visible public forms

                const cannotConfigureAvailability = this._WebsiteManager.IsAvailabilityBookingWidget(websiteBlock.SignupFormType) && (!dialogData.HasTeachersWithAvailabilityAndOnlineBooking || !dialogData.HasVisibleTeachers);
                const cannotConfigureBookFromCalendar = this._WebsiteManager.IsCalendarBookingWidget(websiteBlock.SignupFormType) && (!dialogData.HasOpenLessonSlots || !dialogData.HasVisibleTeachers);
                
                if (cannotConfigureAvailability || cannotConfigureBookFromCalendar) {
                    this.CreateDialog(ConfigurationErrorDialogComponent, dialogData);
                }
            });
		
        }
    }
    
    GetCarouselPrevIndex(currIndex: number, arrLength: number): number {
        return (currIndex-1+arrLength) % arrLength;
    }

    GetCarouselNextIndex(currIndex: number, arrLength: number): number {
        return (currIndex+1) % arrLength;
    }

    GoToHomePage(): void {
        const homePage = this.websiteData.WebsitePages.find(page => page.IsHomepage);
        const homePageID = homePage ? homePage.ID : null;
        this.NavigationPageClicked(homePageID);
    }

    PrepareScalingTypeData(block: WebsiteBlockDisplayItem): WebsiteBlockTypeData {
        if (WebsiteBlockDisplayItem.IsTextAndMedia(block)) {
            return { BlockType: block.WebsiteBlockType, BlockSubtype: block.TextAndMediaType };
        } else if (WebsiteBlockDisplayItem.IsContactForm(block)) {
            return { BlockType: block.WebsiteBlockType, BlockSubtype: block.ContactFormType };
        } else if (WebsiteBlockDisplayItem.IsLoginForm(block)) {
            return { BlockType: block.WebsiteBlockType, BlockSubtype: block.LoginFormType };
        } else {
            throw Error(`Block type does not have image scaling information implemented.`);
        }
    }

    IsV1WebsiteTemplate(template: WebsiteTemplate): boolean {
        return template === WebsiteTemplate.Bold || template === WebsiteTemplate.Minimalistic || template === WebsiteTemplate.Standard;
    }

    CloneBlockToPage(block: WebsiteBlockDisplayItem, page: WebsitePageDisplayItem): void {
        this._SnackbarService.CreateBasicSnackBar(new SnackbarData({
            Data: $localize`:@@TP_WebsiteBuilder_Block_ClonedSuccessfully_SnackbarText:Block cloned successfully!`,
        }));
        const blockCopy = JSON.parse(JSON.stringify(block)) as WebsiteBlockDisplayItem;
        blockCopy.ID = UniqueID();

        const insertIndex = page.ID === this._currentPageID ? block.OrderIndex + 1 : this.websiteBlockCount;
        page.WebsiteBlocks.splice(insertIndex, 0, blockCopy);
        this.NormalizeOrderIndex(page.WebsiteBlocks);


        this.pageChanged.emit();
    }


    private GetBookingSearchOptions(existingEvents: boolean): CalendarBookingSearchOptions {
        return new CalendarBookingSearchOptions({
            ExistingEvents: existingEvents,
            StartDate: SBDate.Today().ToISO(),
            EndDate: SBDate.Today().AddDays(this.renderContext.SignupFormRenderContext?.AdvanceBookingLimitDays!).ToISO(),
            TeacherIDs: this.renderContext.SignupFormRenderContext!.PreferredTeachers.map(tch => tch.ID!),
        });
    }

    private GetPage(ID: string, pages: WebsitePageDisplayItem[]): WebsitePageDisplayItem | null {
        for (let page of pages) {
            if (page.ID === ID) {
                return page;
            }
            const childPage = this.GetPage(ID, page.ChildPages);
            if (childPage) {
                return childPage;
            }
        }
        return null;
    }

    private GetImageURL(ID: string): void {
        if (this.websiteMode === WebsiteMode.Live) {
            this._headerLogoSource = this.websiteData.HeaderLogoUrl;
        } else {
            if (ID) {
                ApiRoutes.WebsiteMediaLibrary.Items.Get.Call({Parameter: ID}).subscribe({
                    next: value => this.SetNewImage(value),
                    error: (_: any) => this.websiteData.HeaderLogoMediaLibraryItemID
                });
            } else {
                this.websiteData.HeaderLogoMediaLibraryItemID = null;
            }
        }
    }

    private SetNewImage(value: WebsiteMediaLibraryDisplayItem): void {
        this._headerLogoSource = value.URL;
    }

    private MoveBlock(block: WebsiteBlockDisplayItem, offset: number) {
        const destination = (block.OrderIndex + this.websiteBlockCount + offset) % this.websiteBlockCount;
        [this._blocks![block.OrderIndex], this._blocks![destination]] = [this._blocks![destination], this._blocks![block.OrderIndex]];
        this.NormalizeOrderIndex();

        this.pageChanged.emit();
        this.animate = true;
    }

    private DeleteBlock(block: WebsiteBlockDisplayItem): void {
        this._blocks!.splice(block.OrderIndex, 1);
        this.NormalizeOrderIndex();
        this.pageChanged.emit();
        this.animate = true;
    }

    //creates a clone of the block with a new ID and inserts it beside the original or onto another page
    private CloneBlock(block: WebsiteBlockDisplayItem): void {
        this.CreateDialog(CloneBlockDialogComponent, block);
    }

    private OpenMediaGalleryDialog(block: WebsiteBlockDisplayItem.WithImages, index: number) {
        const imageIndex: WebsiteBlockImageIndexItem = {
            ImageIndex: index,
            Block: block
        };
        this.openMediaDialog.emit(imageIndex);
    }

    private AddImage(block: WebsiteBlockDisplayItem.WithImages, index: number): void {
        const newImage = new WebsiteBlockTextAndMediaImageDisplayItem({ WebsiteMediaLibraryItemID: UniqueID() });
        if (WebsiteBlockDisplayItem.IsTextAndMedia(block) && WebsiteBlockTextAndMediaDisplayItem.IsGalleryBlock(block)) {
            newImage.Header = $localize`:@@TPWebsiteTextMediaBlock:Heading`;
            newImage.Body = LoremIpsumText.OneSentence;
        }

        block.Images.splice(index, 0, newImage);

        this.pageChanged.emit();
    }

    private DeleteImage(block: WebsiteBlockDisplayItem.WithImages, index: number): void {
        block.Images.splice(index, 1);
        if (block.Images.length < 1) {
            this.AddImage(block, index);
        }

        this.pageChanged.emit();
    }

    private MoveImage(block: WebsiteBlockDisplayItem.WithImages, index: number, offset: number): void {
        const destination = (index + block.Images.length + offset) % block.Images.length;
        [block.Images[index], block.Images[destination]] = [block.Images[destination], block.Images[index]];

        this.pageChanged.emit();
    }

    private OpenImageDetailsDialog(block: WebsiteBlockDisplayItem.WithImages, index: number) {
        const imageIndex: WebsiteBlockImageIndexItem = {
            ImageIndex: index,
            Block: block
        };
        this.openImageDetailsDialog.emit(imageIndex);
    }

    private NormalizeOrderIndex(blocks?: WebsiteBlockDisplayItem[]): void {
        (blocks ?? this._blocks)?.forEach((block, index) => {
            block.OrderIndex = index;
        });
    }

    get blocks(): WebsiteBlockDisplayItem[] {
        return this._blocks!;
    }

    get websiteBlockCount(): number {
        return this._blocks!.length;
    }

    get websiteData(): WebsiteDisplayItem {
        return this._websiteData;
    }

    get pages(): WebsitePageDisplayItem[] {
        return this._websiteData.WebsitePages;
    }

    get WebsiteBlockType(): typeof WebsiteBlockType {
        return WebsiteBlockType;
    }

    get WebsiteBlockDisplayItem(): typeof WebsiteBlockDisplayItem {
        return WebsiteBlockDisplayItem;
    }

    get WebsiteBlockTextAndMediaDisplayItem(): typeof WebsiteBlockTextAndMediaDisplayItem {
        return WebsiteBlockTextAndMediaDisplayItem;
    }

    get WebsiteBlockTextAndMediaType(): typeof WebsiteBlockTextAndMediaType {
        return WebsiteBlockTextAndMediaType;
    }

    get WebsiteBlockContactFormType(): typeof WebsiteBlockContactFormType {
        return WebsiteBlockContactFormType;
    }

    get WebsiteBlockLoginformType(): typeof WebsiteBlockLoginFormType {
        return WebsiteBlockLoginFormType;
    }

    get WebsiteBlockEmbedContentType(): typeof WebsiteBlockEmbedContentType {
        return WebsiteBlockEmbedContentType;
    }

    get WebsiteTemplate(): typeof WebsiteTemplate {
        return WebsiteTemplate;
    }

    get WebsiteMode(): typeof WebsiteMode {
        return WebsiteMode;
    }

    get currentPageID(): string {
        return this._currentPageID;
    }

    get headerLogoSource(): string | null {
        return this._headerLogoSource;
    }

    get socialLinksInHeader(): boolean {
        return this._WebsiteManager.ShowSocialLinksInHeader(this._websiteData.SocialLinksLocation);
    }

    get socialLinksInFooter(): boolean {
        return this._WebsiteManager.ShowSocialLinksInFooter(this._websiteData.SocialLinksLocation);
    }

    get photoGalleryCarouselMaxItems(): number {
        return photoGalleryCarouselMaxItems;
    }

    get scriptCodes(): WebsiteScriptCodeDisplayItem[] {
        return this._websiteData.ScriptCodes!;
    }
}
